import React, { useState, useEffect, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  theme,
  TabView,
  Box,
  Button,
  Modal,
  Panel,
  Text,
  Flex,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import { AuthContext } from '@xometry/auth-context';

import {
  GetOrganizationUsersQuery,
  GetOrganizationEventsQuery,
  GetOrganizationInvitationsQuery,
  Organization,
  UpdateOrganizationMemberRoleByMemberIdMutationResult,
  UpdateOrganizationMemberRoleByMemberIdMutationVariables,
  UpdateOrganizationMemberRoleByMemberIdQuery,
  RemoveOrganizationUsersMutationResult,
  RemoveOrganizationUsersMutationVariables,
  RemoveOrganizationUsersQuery,
  SendOrganizationInvitationsMutationResult,
  SendOrganizationInvitationsMutationVariables,
  SendOrganizationInvitationsQuery,
  UpdateOrganizationInvitationRolesMutationResult,
  UpdateOrganizationInvitationRolesMutationVariables,
  UpdateOrganizationInvitationRolesQuery,
  DeleteInvitationForOrganizationMutationResult,
  DeleteInvitationForOrganizationMutationVariables,
  DeleteInvitationForOrganizationQuery,
  OrganizationUser,
} from '../../../../schema/generated/schema';
import { NotificationContext } from '../../../../contexts/Notification';
import OrganizationMemberHelper, { OrganizationActiveUser } from './helper';

// TabView Content
import BaseTable from './BaseTable';
import { ActiveMemberColumns } from './ActiveMemberColumns';
import { PendingMemberColumns } from './PendingMemberColumns';
import InviteButtons from '../../InviteButtons';
import OrganizationRoleCard from '../../../../components/OrganizationRoleCard';
import EventLog from '../EventLog';

enum TabOptions {
  ACTIVE_MEMBERS,
  PENDING_MEMBERS,
  EVENT_LOG,
}

type TeamMemberManagement = {
  hasWriteOrgPermission: boolean;
  isStaff: boolean;
  org: Organization | null;
  organizationUsers: OrganizationUser[];
  onOnboardingActionCompleted: () => void;
};

const TeamMemberManagement = ({
  hasWriteOrgPermission,
  isStaff,
  org,
  organizationUsers,
  onOnboardingActionCompleted,
}: TeamMemberManagement) => {
  const [activeMembers, setActiveMembers] = useState<OrganizationActiveUser[]>(
    []
  );
  const [pendingMembers, setPendingMembers] = useState([]);
  const [eventLog, setEventLog] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteInvitationsModalOpen, setIsDeleteInvitationsModalOpen] =
    useState(false);
  const [isCurrentUserLeavingModalOpen, setIsCurrentUserLeavingModalOpen] =
    useState(false);
  const [confirmDeleteMembers, setConfirmDeleteMembers] = useState(false);
  const [confirmDeleteInvitations, setConfirmDeleteInvitations] =
    useState(false);
  const [memberIdsToDelete, setMemberIdsToDelete] = useState<string[]>([]);
  const [invitationEmailsToDelete, setInvitationEmailsToDelete] = useState<
    string[]
  >([]);
  const [isCurrentUserOnlyAdmin, setIsCurrentUserOnlyAdmin] = useState(false);

  const [roleCounts, setRoleCounts] = useState<
    {
      role: string;
      count: number;
    }[]
  >([]);

  const [selectedTabView, setSelectedTabView] = useState(
    TabOptions.ACTIVE_MEMBERS
  );

  const { user } = useContext(AuthContext);
  const { addNotification } = useContext(NotificationContext);

  const { data: organizationPendingUsersData } = useQuery(
    GetOrganizationInvitationsQuery,
    {
      variables: {
        orgId: org?.id,
      },
    }
  );

  const { data: getOrganizationEvents } = useQuery(GetOrganizationEventsQuery, {
    variables: {
      orgId: org?.id,
    },
  });

  const [updateRoleMutation] = useMutation<
    UpdateOrganizationMemberRoleByMemberIdMutationResult,
    UpdateOrganizationMemberRoleByMemberIdMutationVariables
  >(UpdateOrganizationMemberRoleByMemberIdQuery);

  const [
    removeMembersMutation,
    { data: removeMembersData, error: removeMembersError },
  ] = useMutation<
    RemoveOrganizationUsersMutationResult,
    RemoveOrganizationUsersMutationVariables
  >(RemoveOrganizationUsersQuery, {
    refetchQueries: [GetOrganizationUsersQuery, 'GetOrganizationUsers'],
  });

  const [updateInvitationRolesMutation] = useMutation<
    UpdateOrganizationInvitationRolesMutationResult,
    UpdateOrganizationInvitationRolesMutationVariables
  >(UpdateOrganizationInvitationRolesQuery);

  const [sendInvitationsMutation] = useMutation<
    SendOrganizationInvitationsMutationResult,
    SendOrganizationInvitationsMutationVariables
  >(SendOrganizationInvitationsQuery);

  const [deleteInvitationMutation] = useMutation<
    DeleteInvitationForOrganizationMutationResult,
    DeleteInvitationForOrganizationMutationVariables
  >(DeleteInvitationForOrganizationQuery, {
    refetchQueries: [
      GetOrganizationInvitationsQuery,
      'GetOrganizationInvitations',
    ],
  });

  const createUpdateNotification = (
    type: 'success' | 'danger',
    msg: string
  ) => {
    addNotification(type, msg);
  };

  useEffect(() => {
    if (organizationUsers) {
      const members =
        OrganizationMemberHelper.formatActiveMembers(organizationUsers);
      // Asynchronously calculate the role counts for a given member list
      const parseRoleCounts = async () => {
        const roleCounts = await OrganizationMemberHelper.countMemberRoles(
          members
        );
        setRoleCounts(roleCounts);
      };
      parseRoleCounts();
      setActiveMembers(members);
    }
    if (organizationPendingUsersData) {
      const members = OrganizationMemberHelper.formatPendingMembers(
        organizationPendingUsersData.getOrganizationInvitations
      );
      setPendingMembers(members);
    }
  }, [organizationUsers, organizationPendingUsersData]);

  useEffect(() => {
    if (confirmDeleteMembers) {
      setConfirmDeleteMembers(false);
      removeMembersMutation({
        variables: {
          orgId: org?.id,
          memberIds: memberIdsToDelete,
        },
      });
    }
  }, [confirmDeleteMembers]);

  useEffect(() => {
    if (getOrganizationEvents) {
      setEventLog(
        getOrganizationEvents.getOrganizationEvents.slice().reverse()
      );
    }
  }, [getOrganizationEvents]);

  useEffect(() => {
    if (confirmDeleteInvitations) {
      setConfirmDeleteInvitations(false);
      invitationEmailsToDelete.forEach((invitationEmail) => {
        deleteInvitationMutation({
          variables: {
            orgId: org?.id,
            memberEmail: invitationEmail,
          },
        });
      });
      setInvitationEmailsToDelete([]);
    }
  }, [confirmDeleteInvitations]);

  useEffect(() => {
    if (removeMembersData) {
      createUpdateNotification(
        'success',
        `${memberIdsToDelete.length} User${
          memberIdsToDelete.length > 1 ? 's' : ''
        } Removed from ${org?.name}`
      );
      setMemberIdsToDelete([]);
    }
  }, [removeMembersData]);

  useEffect(() => {
    if (removeMembersError) {
      createUpdateNotification(
        'danger',
        `Unable to remove team members, wait a few minutes and try again.`
      );
      setMemberIdsToDelete([]);
    }
  }, [removeMembersError]);

  const handleQuickAction = (action: string, selectedRows: string[]) => {
    if (selectedTabView === TabOptions.ACTIVE_MEMBERS) {
      const selectedMembers = activeMembers.filter((member) =>
        selectedRows.includes(member.id)
      );
      if (action === 'Assign as Admin') {
        selectedMembers.forEach((member) => {
          updateRoleMutation({
            variables: {
              orgId: org?.id,
              roleId: '1',
              memberId: member.id,
            },
          });
        });
        createUpdateNotification(
          'success',
          `${selectedMembers.length} Role${
            selectedMembers.length > 1 ? 's' : ''
          } Updated`
        );
      } else if (action === 'Assign as Contributor') {
        selectedMembers.forEach((member) => {
          updateRoleMutation({
            variables: {
              orgId: org?.id,
              roleId: '2',
              memberId: member.id,
            },
          });
        });
        createUpdateNotification(
          'success',
          `${selectedMembers.length} Role${
            selectedMembers.length > 1 ? 's' : ''
          } Updated`
        );
      } else if (action === 'Remove Member(s)') {
        const memberIds = selectedMembers.map((member) => member.id);
        setMemberIdsToDelete(memberIds);
        setIsDeleteModalOpen(true);
      }
    } else {
      const selectedInvitations = pendingMembers.filter((invitation) =>
        selectedRows.includes(invitation.id.toString())
      );
      if (action === 'Assign as Admin') {
        updateInvitationRolesMutation({
          variables: {
            orgId: org?.id,
            roleId: '1',
            emails: selectedInvitations.map(
              (invitation) => invitation.userData.email
            ),
          },
        });
        createUpdateNotification(
          'success',
          `${selectedInvitations.length} Role${
            selectedInvitations.length > 1 ? 's' : ''
          } Updated`
        );
      } else if (action === 'Assign as Contributor') {
        updateInvitationRolesMutation({
          variables: {
            orgId: org?.id,
            roleId: '2',
            emails: selectedInvitations.map(
              (invitation) => invitation.userData.email
            ),
          },
        });
        createUpdateNotification(
          'success',
          `${selectedInvitations.length} Role${
            selectedInvitations.length > 1 ? 's' : ''
          } Updated`
        );
      } else if (action === 'Resend Invitation') {
        sendInvitationsMutation({
          variables: {
            addUsersInput: {
              organizationId: org?.id,
              emails: selectedInvitations.map(
                (invitation) => invitation.userData.email
              ),
              permissionIds: [],
            },
          },
        });
        createUpdateNotification(
          'success',
          `${selectedInvitations.length} Invitation${
            selectedInvitations.length > 1 ? 's' : ''
          } sent`
        );
      } else if (action === 'Remove Invitation') {
        const emails = selectedInvitations.map(
          (invitation) => invitation.userData.email
        );
        setInvitationEmailsToDelete(emails);
        setIsDeleteInvitationsModalOpen(true);
        createUpdateNotification(
          'success',
          `${selectedInvitations.length} Invitation${
            selectedInvitations.length > 1 ? 's' : ''
          } Removed`
        );
      }
    }

    if (window.analytics && user) {
      window.analytics.track('Team member management quick action updated', {
        isStaff,
        quickAction: `${selectedTabView}-${action}`,
        userId: user.id,
        organizationId: org?.id,
        email: user.emailAddress,
      });
    }
  };

  const handleLeaveTeam: any = () => {
    const isOnlyAdmin = OrganizationMemberHelper.isCurrentUserOnlyAdmin(
      activeMembers,
      user?.emailAddress
    );
    if (isOnlyAdmin) {
      setIsCurrentUserOnlyAdmin(isOnlyAdmin);
    } else {
      const member = activeMembers.find((m) => m.email === user?.emailAddress);
      if (member) {
        setMemberIdsToDelete([member.id]);
        setIsCurrentUserLeavingModalOpen(true);
      }
    }
  };

  return (
    <Flex flexDirection="column" marginTop={[theme.space[9]]}>
      <Box>
        <Flex
          className="member-table-header"
          justifyContent="space-between"
          alignItems="center"
          paddingBottom={theme.space[6]}
          flexDirection={['column', 'column', 'row']}
        >
          <Flex
            flexDirection="column"
            width={['100%', '100%', '100%', '50%']}
            alignItems={['center', 'flex-start']}
            marginBottom={['12px', '0']}
          >
            <Flex>
              <Text kind="panel-title">Team Member Management</Text>
            </Flex>
            <Flex>
              <Text fontSize="13px">
                Invite and manage team members and their roles.
              </Text>
            </Flex>
          </Flex>
          <Flex flexDirection={['column', 'row']} gap={theme.space[3]}>
            {!org?.isArchived && (
              <InviteButtons
                orgName={org?.name}
                orgId={org?.id}
                hasWriteOrgPermission={hasWriteOrgPermission}
                onOnboardingActionCompleted={onOnboardingActionCompleted}
              />
            )}
          </Flex>
        </Flex>
      </Box>
      {roleCounts.length > 0 && (
        <Flex
          gap={`${theme.space[4]}`}
          flexWrap="wrap"
          marginBottom={theme.space[4]}
        >
          {roleCounts.map((roleCount) => (
            <Box
              key={roleCount.role}
              width={['100%', `calc(50% - ${theme.space[2]})`]}
            >
              <OrganizationRoleCard {...roleCount} />
            </Box>
          ))}
        </Flex>
      )}
      <Flex marginBottom={theme.space[4]}>
        <Box width="100%">
          <Panel kind="primary" padding="0" overflowX="auto">
            <TabView width={['fit-content', '100%']} className="Tabita">
              <TabView.Header>
                <TabView.Header.Tab
                  width="198px"
                  height="50px"
                  borderRadius={`${theme.space[1]} 0 0 0`}
                >
                  <Box
                    onClick={() => {
                      setSelectedTabView(TabOptions.ACTIVE_MEMBERS);
                    }}
                  >
                    Team Members
                  </Box>
                </TabView.Header.Tab>
                <TabView.Header.Tab
                  width="198px"
                  height="50px"
                  borderRadius={`${theme.space[1]} 0 0 0`}
                >
                  <Box
                    onClick={() => {
                      setSelectedTabView(TabOptions.PENDING_MEMBERS);
                    }}
                  >
                    Invitations{' '}
                    {pendingMembers.length > 0 && ` (${pendingMembers.length})`}
                  </Box>
                </TabView.Header.Tab>
                <TabView.Header.Tab
                  width="198px"
                  height="50px"
                  borderRadius={`${theme.space[1]} 0 0 0`}
                >
                  <Box
                    onClick={() => {
                      setSelectedTabView(TabOptions.EVENT_LOG);
                    }}
                  >
                    Event Log
                  </Box>
                </TabView.Header.Tab>
              </TabView.Header>
              <TabView.Content>
                <TabView.Content.Item>
                  <BaseTable
                    members={activeMembers}
                    MemberColumns={ActiveMemberColumns}
                    hasWriteOrgPermission={hasWriteOrgPermission}
                    isStaff={isStaff}
                    handleLeaveTeam={handleLeaveTeam}
                    currentEmail={user.emailAddress}
                    handleQuickAction={handleQuickAction}
                    isReadOnly={org?.isArchived || false}
                    quickActions={[
                      'Assign as Admin',
                      'Assign as Contributor',
                      'Remove Member(s)',
                    ]}
                  />
                </TabView.Content.Item>
                <TabView.Content.Item>
                  {pendingMembers.length > 0 ? (
                    <BaseTable
                      members={pendingMembers}
                      MemberColumns={PendingMemberColumns}
                      hasWriteOrgPermission={hasWriteOrgPermission}
                      isStaff={isStaff}
                      currentEmail={'N/A'}
                      handleLeaveTeam={handleLeaveTeam}
                      handleQuickAction={handleQuickAction}
                      isReadOnly={org?.isArchived || false}
                      quickActions={[
                        'Assign as Admin',
                        'Assign as Contributor',
                        'Resend Invitation',
                        'Remove Invitation',
                      ]}
                    />
                  ) : (
                    <Flex
                      flexDirection="column"
                      alignItems="center"
                      width={['330px', '680px']}
                      paddingY="86px"
                    >
                      <img
                        src={`https://assets.xometry.com/images/spot-add-user.svg`}
                        css={css`
                          width: 270px;
                        `}
                        alt="No Pending Invitations"
                      />
                      <Box
                        color={theme.colors.darkBlues[90]}
                        fontSize={theme.fontSizes[2]}
                        paddingTop={theme.space[5]}
                      >
                        Invited Team Members will display here.
                      </Box>
                    </Flex>
                  )}
                </TabView.Content.Item>
                <TabView.Content.Item>
                  <EventLog
                    events={eventLog}
                    orgName={org?.name || ''}
                    orgUsers={organizationUsers}
                  />
                </TabView.Content.Item>
              </TabView.Content>
            </TabView>
          </Panel>
        </Box>
      </Flex>
      <Modal
        isOpen={isDeleteModalOpen}
        breakpointMaxWidth="634px"
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <Box padding={theme.space[6]}>
          <Flex justifyContent="space-between">
            <Flex
              lineHeight="44px"
              color={theme.colors.darkBlues[90]}
              fontSize={theme.fontSizes[4]}
              justifyContent="left"
              marginBottom={theme.space[3]}
            >
              Remove team members?
            </Flex>
            <Modal.CloseButton />
          </Flex>
          <Flex>
            <Text kind="body">
              Removed team members will no longer have access to quotes and
              orders associated with the Team they are being removed from.
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" marginTop={theme.space[6]}>
            <>
              <Button
                fontWeight={theme.fontWeights.regular}
                marginRight={theme.space[2]}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                kind={theme.kind.danger}
                fontWeight={theme.fontWeights.regular}
                onClick={() => {
                  setConfirmDeleteMembers(true);
                  setIsDeleteModalOpen(false);
                }}
              >
                Remove
              </Button>
            </>
          </Flex>
        </Box>
      </Modal>
      <Modal
        isOpen={isDeleteInvitationsModalOpen}
        breakpointMaxWidth="634px"
        onClose={() => setIsDeleteInvitationsModalOpen(false)}
      >
        <Box padding={theme.space[6]}>
          <Flex justifyContent="space-between">
            <Flex
              lineHeight="44px"
              color={theme.colors.darkBlues[90]}
              fontSize={theme.fontSizes[4]}
              justifyContent="left"
              marginBottom={theme.space[3]}
            >
              Remove team members invitations?
            </Flex>
            <Modal.CloseButton />
          </Flex>
          <Flex>
            <Text kind="body">
              Once an invitation to a team member has been removed, they will
              not be able to join or access your Team.
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" marginTop={theme.space[6]}>
            <>
              <Button
                fontWeight={theme.fontWeights.regular}
                marginRight={theme.space[2]}
                onClick={() => setIsDeleteInvitationsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                kind={theme.kind.danger}
                fontWeight={theme.fontWeights.regular}
                onClick={() => {
                  setConfirmDeleteInvitations(true);
                  setIsDeleteInvitationsModalOpen(false);
                }}
              >
                Remove
              </Button>
            </>
          </Flex>
        </Box>
      </Modal>
      {isCurrentUserOnlyAdmin && (
        <Modal
          isOpen={isCurrentUserOnlyAdmin}
          padding="40px"
          onClose={() => setIsCurrentUserOnlyAdmin(false)}
          breakpointMaxWidth="634px"
        >
          <Flex justifyContent="space-between">
            <Flex
              lineHeight="44px"
              color={theme.colors.darkBlues[90]}
              fontSize={theme.fontSizes[4]}
              justifyContent="left"
              marginBottom={theme.space[3]}
            >
              Unable to Leave {org?.name}
            </Flex>
            <Modal.CloseButton />
          </Flex>
          <Flex>
            <Text kind="body">
              You are unable to leave the team because you are the only Admin.
              In order to leave {org?.name}, you will need to make another team
              member an Admin.
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" marginTop={theme.space[6]}>
            <Button
              kind={theme.kind.neutral}
              fontWeight={theme.fontWeights.regular}
              onClick={() => setIsCurrentUserOnlyAdmin(false)}
            >
              Return to Team Settings
            </Button>
          </Flex>
        </Modal>
      )}
      {isCurrentUserLeavingModalOpen && (
        <Modal
          isOpen={isCurrentUserLeavingModalOpen}
          padding="40px"
          onClose={() => setIsCurrentUserLeavingModalOpen(false)}
          breakpointMaxWidth="634px"
        >
          <Flex justifyContent="space-between">
            <Flex
              lineHeight="44px"
              color={theme.colors.darkBlues[90]}
              fontSize={theme.fontSizes[4]}
              justifyContent="left"
              marginBottom={theme.space[3]}
            >
              Leave {org?.name}?
            </Flex>
            <Modal.CloseButton />
          </Flex>
          <Flex>
            <Text kind="body">
              You will no longer have access to quotes and orders associated
              with this Team.
            </Text>
          </Flex>
          <Flex justifyContent="flex-end" marginTop={theme.space[6]}>
            <>
              <Button
                fontWeight={theme.fontWeights.regular}
                marginRight={theme.space[2]}
                onClick={() => setIsCurrentUserLeavingModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                kind={theme.kind.danger}
                fontWeight={theme.fontWeights.regular}
                onClick={() => {
                  setConfirmDeleteMembers(true);
                  setIsCurrentUserLeavingModalOpen(false);
                  window.location.href = `/user-settings?msg=You have left the ${org?.name} Team.&type=success`;
                }}
              >
                Leave
              </Button>
            </>
          </Flex>
        </Modal>
      )}
    </Flex>
  );
};

TeamMemberManagement.defaultProps = {
  hasWriteOrgPermission: false,
  isStaff: false,
  org: null,
};

export default TeamMemberManagement;
